import { TApiReportsDate } from "@timenotes/shared/src/services/api-client/reports-api"
import { DatePicker, Select } from "antd"
import { PickerDateProps } from "antd/lib/date-picker/generatePicker"
import { useDefaultDateFormat } from "hooks/settings"
import { first } from "lodash"
import moment, { Moment } from "moment-timezone"
import { toTitleCase } from "utils/string-utils"

export interface IReportsDatePicker extends Omit<PickerDateProps<Moment>, 'onChange'> {
  timespanOptions?: TApiReportsDate['timespan'][]
  reportsDate: TApiReportsDate
  onChange(value: TApiReportsDate): void
}

const TimespanPicker = ({
  timespan,
  timespanOptions,
  onChange,
}: {
  timespan: TApiReportsDate['timespan'],
  timespanOptions?: TApiReportsDate['timespan'][]
  onChange(value: TApiReportsDate['timespan']): void,
},
) => {

  let options: TApiReportsDate['timespan'][] = ['day', 'week', 'month', 'year', 'custom', 'relative']

  if (timespanOptions) {
    options = options.filter((option) => { return timespanOptions.includes(option)})
  }

  const selectOptions = options.map((option) => (
    <Select.Option key={option} value={option}>{toTitleCase(option)}</Select.Option>
  ))

  return (
    <Select
      value={timespan}
      onChange={(value) => { onChange(value) }}
      style={{ width: '100px' }}
    >
      {selectOptions}
   </Select>
  )
}

export const ReportsDatePickerInput = (props: IReportsDatePicker): JSX.Element => {
  const { defaultDateFormat } = useDefaultDateFormat()

  const timespan = props.reportsDate.timespan
  const timespanOptions = props.timespanOptions

  switch (timespan) {
    case 'day':
      return (
        <DatePicker
          {...props}
          format={defaultDateFormat}
          value={moment(props.reportsDate.from, 'DD.MM.yyyyy')}
          onChange={(value) => {
            props.onChange({
              ...props.reportsDate,
              from: value?.format('DD.MM.yyyy') || moment().format('DD.MM.yyyy'),
              customRange: undefined,
            })
          }}
        />
      )
      break
    case 'week':
      return (
        <DatePicker.WeekPicker
          {...props}
          format={defaultDateFormat}
          value={moment(props.reportsDate.from, 'DD.MM.yyyy')}
          onChange={(value) => {
            props.onChange({
              ...props.reportsDate,
              from: value?.format('DD.MM.yyyy') || moment().format('DD.MM.yyyy'),
              customRange: undefined,
            })
          }}
        />
      )
      break
    case 'month':
      return (
        <DatePicker.MonthPicker
          {...props}
          format={'MMM yyyy'}
          value={moment(props.reportsDate.from, 'DD.MM.yyyy')}
          onChange={(value) => {
            props.onChange({
              ...props.reportsDate,
              from: value?.format('DD.MM.yyyy') || moment().format('DD.MM.yyyy'),
              customRange: undefined,
            })
          }}
        />
      )
      break
    case 'year':
      return (
        <DatePicker.YearPicker
          {...props}
          format={'yyyy'}
          value={moment(props.reportsDate.from, 'DD.MM.yyyy')}
          onChange={(value) => {
            props.onChange({
              ...props.reportsDate,
              from: value?.format('DD.MM.yyyy') || moment().format('DD.MM.yyyy'),
              customRange: undefined,
            })
          }}
        />
      )
      break
    case 'custom':
      return (
        <DatePicker.RangePicker
          format={defaultDateFormat}
          value={[moment(props.reportsDate.from, 'DD.MM.yyyy'), moment(props.reportsDate.to, 'DD.MM.yyyy')]}
          onChange={(values) => {
            if (values) {
              props.onChange({
                ...props.reportsDate,
                from: values[0]?.format('DD.MM.yyyy') || moment().format('DD.MM.yyyy'),
                to: values[1]?.format('DD.MM.yyyy') || undefined,
                customRange: undefined,
              })
            }
          }}
        />
      )
      break
    case 'relative':
      return (
        <Select
          style={{ width: '132px' }} // match date pickers
          placeholder="Select range"
          value={props.reportsDate.customRange || getDefaultTimespanOption(timespanOptions)}
          onChange={(value) => {
            props.onChange({
              ...props.reportsDate,
              customRange: value,
            })
          }}
        >
          {(!timespanOptions || timespanOptions.includes('day')) && (
            <>
              <Select.Option value="today">Today</Select.Option>
              <Select.Option value="yesterday">Yesterday</Select.Option>
            </>
          )}

          {(!timespanOptions || timespanOptions.includes('week')) && (
            <>
              <Select.Option value="this_week">This Week</Select.Option>
           </>
          )}

          {!timespanOptions && ( // as a hack make it work only for no limits
            <>
              <Select.Option value="last_week">Last Week</Select.Option>
              <Select.Option value="past_2_weeks">Past 2 Weeks</Select.Option>
            </>
          )}

          {(!timespanOptions || timespanOptions.includes('month')) && (
            <>
              <Select.Option value="this_month">This Month</Select.Option>
              <Select.Option value="last_month">Last Month</Select.Option>
            </>
          )}

          {(!timespanOptions || timespanOptions.includes('year')) && (
            <>
              <Select.Option value="this_year">This Year</Select.Option>
              <Select.Option value="last_year">Last Year</Select.Option>
            </>
          )}
        </Select>
      )
      break
      defaut:
      return (
        <DatePicker />
      )
  }
}

const ReportsDatePicker = (props: IReportsDatePicker): JSX.Element => {

  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', gap: '10px' }}>
      <TimespanPicker
        timespanOptions={props.timespanOptions}
        timespan={props.reportsDate.timespan}
        onChange={(value) => {
          props.onChange({
            ...props.reportsDate,
            timespan: value,
            ...({ customRange: value == 'relative' ? getDefaultTimespanOption(props.timespanOptions) : undefined })
            // for relative setup default customRange based on default timespan option or undefined for other timespans
          })
        }}
      />
      <ReportsDatePickerInput
        {...props}
      />
    </div>
  )
}

const getDefaultTimespanOption = (timespanOptions?: TApiReportsDate['timespan'][]) => {
  let firstTimespanOption: TApiReportsDate['customRange'] = 'today' // default

  if (timespanOptions && timespanOptions.length > 0) {
    firstTimespanOption = {
      day: 'today',
      week: 'this_week',
      month: 'this_month',
      year: 'this_year',
    }[timespanOptions[0]]
  }

  return firstTimespanOption
}

export default ReportsDatePicker